// src/features/WasherAnalysis/WasherAnalysisPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import { 
  Visibility, 
  CheckCircle, 
  Cancel,
  Analytics,
  LocalLaundryService,
  TrendingUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useWasherData, WASHER_PROGRAMS } from './hooks/useWasherData';

const ProgramCard = ({ programKey, data, statistics, config, onViewDetails }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const isRejection = config.type === 'rejection';
  const isWeight = config.type === 'weight';
  const Icon = isRejection ? Cancel : isWeight ? TrendingUp : CheckCircle;

  // Останні 3 записи
  const recentData = data.slice(-3).reverse();

  const getColor = () => {
    if (isRejection) return 'error';
    if (isWeight) return 'success';
    return 'primary';
  };

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px 0 rgba(0,0,0,0.12)',
            border: `1px solid ${alpha(theme.palette[getColor()].main, 0.3)}`,
          }
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Заголовок програми */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${theme.palette[getColor()].main} 0%, ${theme.palette[getColor()].dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette[getColor()].main, 0.3)}`
                }}
              >
                <Icon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {config.label}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={config.unit} 
                    size="small"
                    color={getColor()}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={isRejection ? t('washer_type_rejection') : isWeight ? t('washer_type_weight') : t('washer_type_cycles')}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Статистика */}
          {data.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('washer_stats_total')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette[getColor()].main }}>
                    {statistics.total.toLocaleString('es-ES')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('washer_stats_avg')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {statistics.average.toFixed(1)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Останні 3 записи */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              {t('washer_analysis_latest_readings')}
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                      {t('washer_analysis_date')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                      {config.unit}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentData.length > 0 ? (
                    recentData.map((record, index) => (
                      <TableRow 
                        key={index} 
                        sx={{ 
                          '&:last-child td': { borderBottom: 0 },
                          '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.05) }
                        }}
                      >
                        <TableCell sx={{ py: 1, fontSize: '0.8rem' }}>
                          {new Date(record.date).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit'
                          })}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Chip 
                            label={record.value.toLocaleString('es-ES')} 
                            size="small"
                            color={getColor()}
                            variant={record.value > 0 ? "filled" : "outlined"}
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              minWidth: '60px'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        <Typography variant="body2">
                          {t('washer_analysis_no_data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Інформація */}
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="body2" color="text.secondary">
              {t('washer_stats_days_with_data')}: <strong>{statistics.daysWithData}</strong>
            </Typography>
          </Box>

          {/* Кнопка деталей */}
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button 
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => onViewDetails(programKey)}
              sx={{
                borderRadius: '8px',
                py: 1,
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {t('washer_analysis_view_details')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default function WasherAnalysisPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [programType, setProgramType] = useState('all');

  const { allProgramsData, loading, error } = useWasherData();

  // Фільтруємо програми за типом
  const filteredPrograms = useMemo(() => {
    const programs = Object.keys(WASHER_PROGRAMS);
    
    if (programType === 'all') return programs;
    return programs.filter(programKey => 
      WASHER_PROGRAMS[programKey].type === programType
    );
  }, [programType]);

  const handleViewDetails = (programKey) => {
    navigate(`/washer-analysis/${programKey}`);
  };

  // Статистика по всіх програмах
  const totalStats = useMemo(() => {
    const cyclesPrograms = Object.keys(WASHER_PROGRAMS).filter(key => WASHER_PROGRAMS[key].type === 'cycles');
    const weightPrograms = Object.keys(WASHER_PROGRAMS).filter(key => WASHER_PROGRAMS[key].type === 'weight');
    const rejectionPrograms = Object.keys(WASHER_PROGRAMS).filter(key => WASHER_PROGRAMS[key].type === 'rejection');
    
    return {
      totalPrograms: Object.keys(WASHER_PROGRAMS).length,
      cyclesPrograms: cyclesPrograms.length,
      weightPrograms: weightPrograms.length,
      rejectionPrograms: rejectionPrograms.length,
      programsWithData: Object.keys(allProgramsData).filter(key => allProgramsData[key]?.data?.length > 0).length
    };
  }, [allProgramsData]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок сторінки */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <LocalLaundryService sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              {t('washer_analysis_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('washer_analysis_subtitle')}
            </Typography>
          </Box>
        </Stack>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.primary.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {totalStats.totalPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('washer_analysis_programs')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.info.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main' }}>
                  {totalStats.cyclesPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('washer_type_cycles')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                  {totalStats.weightPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('washer_type_weight')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.error.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'error.main' }}>
                  {totalStats.rejectionPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('washer_type_rejection')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Фільтр */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('washer_analysis_filter_type')}</InputLabel>
                <Select
                  value={programType}
                  label={t('washer_analysis_filter_type')}
                  onChange={(e) => setProgramType(e.target.value)}
                >
                  <MenuItem value="all">{t('washer_type_all')}</MenuItem>
                  <MenuItem value="cycles">{t('washer_type_cycles')}</MenuItem>
                  <MenuItem value="weight">{t('washer_type_weight')}</MenuItem>
                  <MenuItem value="rejection">{t('washer_type_rejection')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('washer_analysis_found')}: <strong>{filteredPrograms.length}</strong> {t('washer_analysis_programs')}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Список програм */}
      <Grid container spacing={3}>
        {filteredPrograms.map(programKey => (
          <ProgramCard
            key={programKey}
            programKey={programKey}
            data={allProgramsData[programKey]?.data || []}
            statistics={allProgramsData[programKey]?.statistics || {
              total: 0, average: 0, max: 0, min: 0, daysWithData: 0, recordsCount: 0
            }}
            config={WASHER_PROGRAMS[programKey]}
            onViewDetails={handleViewDetails}
          />
        ))}
      </Grid>

      {/* Інформаційний блок */}
      {totalStats.programsWithData > 0 && (
        <Card 
          sx={{ 
            mt: 4, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {t('washer_analysis_click_for_details')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


